import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconBadge } from '@monksflow/monks-ui';
import { LightbulbIcon, PackageIcon, TrendingUpIcon } from 'lucide-react';

import {
  AnalysisDetailBullets,
  AnalysisDetailDialog,
  AnalysisSubHeader,
} from '../../../../components';
import type { PathmaticsMediaInsights } from '../../model/pathmaticsData.schema';

interface AiOverviewProps {
  insights: PathmaticsMediaInsights;
  /** Scope and freshness line, e.g. "5 brands · generated 17 Aug 2026". */
  meta: string;
}

type SectionKey = 'mediaMixStrategy' | 'flightingStrategy' | 'whiteSpace';

export const AiOverview = ({ insights, meta }: AiOverviewProps) => {
  const { t } = useTranslation('analysis');
  const [openSection, setOpenSection] = useState<SectionKey | null>(null);

  if (!insights) return null;

  const sections: Array<{ key: SectionKey; title: string; icon: ReactNode }> = [
    // IconBadge sizes the icon it holds, so these carry no size of their own.
    {
      key: 'mediaMixStrategy',
      title: t('analysisResult.mediaLandscape.mediaMixStrategy'),
      icon: <PackageIcon />,
    },
    {
      key: 'flightingStrategy',
      title: t('analysisResult.mediaLandscape.flightingStrategy'),
      icon: <TrendingUpIcon />,
    },
    {
      key: 'whiteSpace',
      title: t('analysisResult.mediaLandscape.whiteSpaceOpportunities'),
      icon: <LightbulbIcon />,
    },
  ];

  const open = openSection ? sections.find((s) => s.key === openSection) : null;

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader
        title={t('analysisResult.mediaLandscape.aiOverview')}
        subtitle={meta}
        subtitleTone="meta"
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {sections.map((section) => (
          <article
            key={section.key}
            className="flex flex-col gap-3 rounded-xl border border-border-light bg-card p-xl shadow-sm"
          >
            <div className="flex flex-col items-start gap-3">
              <IconBadge variant="secondary">{section.icon}</IconBadge>
              <span className="text-base font-medium text-foreground">{section.title}</span>
            </div>
            <p className="flex-1 text-pretty text-sm leading-5 text-foreground-alt">
              {insights[section.key].headline}
            </p>
            <div>
              <Button variant="outline" size="sm" onClick={() => setOpenSection(section.key)}>
                {t('analysisResult.mediaLandscape.readMore')}
              </Button>
            </div>
          </article>
        ))}
      </div>

      <AnalysisDetailDialog
        open={open !== null}
        onClose={() => setOpenSection(null)}
        title={open?.title}
        insight={open && insights[open.key].headline}
      >
        {open && <AnalysisDetailBullets items={insights[open.key].bullets} />}
      </AnalysisDetailDialog>
    </div>
  );
};
